import { PdfTemplateType, XmlTemplateType } from "../../dentlab/src/pdf-templates/document-template.types";

export type FileType = "pdf" | "xml" | "img" | "txt" | "video" | "3d" | null;

export const getFileType = (
	fileMetaData: Record<string, any> | null,
	fileName: string | null
): FileType => {
	if (
		fileMetaData &&
		"type" in fileMetaData &&
		["pdf", "xml", "txt", "video", "3d", "img"].includes(fileMetaData.type)
	) {
		return fileMetaData.type;
	}
	if (fileMetaData && "mimeType" in fileMetaData) {
		// If for some reason the mimeType got saved without the type we can fall back to re-detecting the file type
		return detectFileTypeFromMime(fileMetaData.mimeType);
	}

	const potentialFileExt = fileName?.split(".").pop()?.toLowerCase();
	if (potentialFileExt) {
		if (["pdf", "xml"].includes(potentialFileExt)) {
			return potentialFileExt as FileType;
		}
		// Image formats
		else if (
			["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(
				potentialFileExt
			)
		) {
			return "img";
		}
		// Document formats
		else if (
			["txt", "doc", "docx", "odt", "rtf"].includes(potentialFileExt)
		) {
			return "txt";
		}
		// Video formats
		else if (
			["mp4", "webm", "mov", "avi", "mkv", "flv", "wmv"].includes(
				potentialFileExt
			)
		) {
			return "video";
		}
		// 3D formats
		else if (
			["stl", "obj", "ply", "3ds", "fbx", "dae"].includes(
				potentialFileExt
			)
		) {
			return "3d";
		}
	}

	return null;
};

export type FileTypeWithDocumentType =
	| FileType
	| PdfTemplateType.LIEFERSCHEIN
	| PdfTemplateType.KOSTENVORANSCHLAG
	| PdfTemplateType.GUTSCHRIFT;

export const getFileTypeWithDocumentType = (
	fileMetaData: Record<string, any> | null,
	fileName: string | null
): FileTypeWithDocumentType => {
	// We save this property in the file metadata sometimes, so if it exists, we can shortcut to this
	if (fileMetaData && "pdf_template_type" in fileMetaData) {
		if (
			[
				PdfTemplateType.KOSTENVORANSCHLAG,
				PdfTemplateType.LIEFERSCHEIN,
				PdfTemplateType.GUTSCHRIFT,
			].includes(fileMetaData.pdf_template_type)
		) {
			return fileMetaData.pdf_template_type;
		}
	}
	if (fileName?.toLowerCase().includes("lieferschein")) {
		return PdfTemplateType.LIEFERSCHEIN;
	} else if (fileName?.toLowerCase().includes("kostenvoranschlag")) {
		return PdfTemplateType.KOSTENVORANSCHLAG;
	} else if (fileName?.toLowerCase().includes("gutschrift")) {
		return PdfTemplateType.GUTSCHRIFT;
	} else {
		return getFileType(fileMetaData, fileName);
	}
};

/**
 * Detects file type from MIME type
 */
export const detectFileTypeFromMime = (mimeType: string): FileType => {
	if (!mimeType) return null;

	const mimeTypeLower = mimeType.toLowerCase();

	if (mimeTypeLower === "application/pdf") return "pdf";
	if (mimeTypeLower === "application/xml" || mimeTypeLower === "text/xml")
		return "xml";
	if (mimeTypeLower.startsWith("image/")) return "img";

	if (mimeTypeLower.startsWith("video/")) return "video";

	if (
		mimeTypeLower.startsWith("text/") ||
		mimeTypeLower === "application/msword" ||
		mimeTypeLower ===
		"application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
		mimeTypeLower === "application/vnd.oasis.opendocument.text" ||
		mimeTypeLower === "application/rtf"
	) {
		return "txt";
	}

	// 3D files - these often have application/octet-stream, so we still need extension fallback
	if (
		mimeTypeLower === "model/stl" ||
		mimeTypeLower === "application/sla" ||
		mimeTypeLower === "application/vnd.ms-pki.stl" ||
		mimeTypeLower === "application/x-navistyle"
	) {
		return "3d";
	}

	return null;
};

/**
 * Creates metadata object for file uploads
 */
export const createFileMetadata = (
	file: File | { type?: string; size?: number; name?: string },
	pdfTemplateType?: PdfTemplateType | XmlTemplateType
) => {
	let detectedType: FileType | null = null;
	let mimeType: string | null = null;
	if (file.type && ["pdf", "xml", "txt", "video", "3d", "img"].includes(file.type)) {
		detectedType = file.type as FileType;
	} else {
		detectedType = detectFileTypeFromMime(file.type || "application/octet-stream");
		mimeType = file.type || "application/octet-stream";
	}

	const finalType = detectedType || getFileType(null, file.name || null);

	return {
		type: finalType,
		mimeType: mimeType,
		size: file.size || 0,
		pdf_template_type: pdfTemplateType,
	};
};
